/**
 * GSM 7-bit Character Set Validation & Length Calculation Utility
 *
 * GSM 7-bit Basic Characters (each counts as 1 character):
 * - Control / Whitespace: \n, \r, Space
 * - Digits: 0-9
 * - Letters: A-Z, a-z
 * - Symbols & Accents: @ £ $ ¥ è é ù ì ò Ç Ø ø Å å Δ _ Φ Γ Λ Ω Π Ψ Σ Θ Ξ ! " # ¤ % & ' ( ) * + , - . / : ; < = > ? ¡ Ä Ö Ñ Ü § ¿ ä ö ñ ü à
 *
 * GSM 7-bit Extension Characters (each counts as 2 characters toward length limits):
 * - \f, ^, {, }, \, [, ~, ], |, €
 */

const GSM7_BASIC_SET = new Set([
    '@',
    '£',
    '$',
    '¥',
    'è',
    'é',
    'ù',
    'ì',
    'ò',
    'Ç',
    '\n',
    'Ø',
    'ø',
    '\r',
    'Å',
    'å',
    'Δ',
    '_',
    'Φ',
    'Γ',
    'Λ',
    'Ω',
    'Π',
    'Ψ',
    'Σ',
    'Θ',
    'Ξ',
    ' ',
    '!',
    '"',
    '#',
    '¤',
    '%',
    '&',
    "'",
    '(',
    ')',
    '*',
    '+',
    ',',
    '-',
    '.',
    '/',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    ':',
    ';',
    '<',
    '=',
    '>',
    '?',
    '¡',
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
    'Ä',
    'Ö',
    'Ñ',
    'Ü',
    '§',
    '¿',
    'a',
    'b',
    'c',
    'd',
    'e',
    'f',
    'g',
    'h',
    'i',
    'j',
    'k',
    'l',
    'm',
    'n',
    'o',
    'p',
    'q',
    'r',
    's',
    't',
    'u',
    'v',
    'w',
    'x',
    'y',
    'z',
    'ä',
    'ö',
    'ñ',
    'ü',
    'à',
]);

const GSM7_EXTENDED_SET = new Set(['\f', '^', '{', '}', '\\', '[', '~', ']', '|', '€']);

/**
 * Checks if a string contains any character outside the GSM 7-bit basic and extension tables.
 * Blocks emojis, smart quotes (“ ” ‘ ’), em-dash (–), bullets, non-Latin chars, etc.
 *
 * @param {string} text - The input text to validate
 * @returns {boolean} True if text contains invalid GSM-7 characters, false if valid
 */
export const hasInvalidGSM7Characters = (text) => {
    if (!text) return false;
    for (const char of text) {
        if (!GSM7_BASIC_SET.has(char) && !GSM7_EXTENDED_SET.has(char)) {
            return true;
        }
    }
    return false;
};

/**
 * Calculates total character count according to GSM 7-bit rules.
 * Basic characters count as 1, extended characters count as 2.
 *
 * @param {string} text - The input text to measure
 * @returns {number} Character count
 */
export const getGSM7Length = (text) => {
    if (!text) return 0;
    let length = 0;
    for (const char of text) {
        if (GSM7_EXTENDED_SET.has(char)) {
            length += 2;
        } else {
            length += 1;
        }
    }
    return length;
};

/**
 * Normalizes non-breaking spaces (\u00A0 -> ' '), converts smart quotes and dashes
 * to standard GSM-7 characters, and strips emojis, bullets, and non-GSM7 symbols.
 *
 * @param {string} text - Raw input text
 * @returns {string} Sanitized GSM-7 compliant string
 */
export const sanitizeGSM7Text = (text) => {
    if (!text) return '';
    const result = text
        .replace(/\u00A0/g, ' ')
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/\u2026/g, '...');

    let cleaned = '';
    for (const char of result) {
        if (GSM7_BASIC_SET.has(char) || GSM7_EXTENDED_SET.has(char)) {
            cleaned += char;
        }
    }
    return cleaned;
};

/**
 * Retrieves list of missing required variables for a template item.
 *
 * @param {Object} item - Template item object containing requiredVariables or variables
 * @param {string} currentMsg - Current message template string
 * @returns {Array<string>} List of missing required variable strings formatted like '{shop name}'
 */
export const getMissingRequiredVariables = (item, currentMsg) => {
    if (!item || currentMsg === undefined || currentMsg === null) return [];

    let reqVarNames = [];
    if (Array.isArray(item.requiredVariables)) {
        reqVarNames = item.requiredVariables;
    } else if (Array.isArray(item.variables)) {
        reqVarNames = item.variables.filter((v) => v.isRequired).map((v) => v.varName);
    }

    if (!reqVarNames || reqVarNames.length === 0) return [];

    const missing = [];
    reqVarNames.forEach((varName) => {
        const cleanedVar = varName.replace(/[{}]/g, '');
        const pattern = `\\{${cleanedVar.replace(/_/g, '[_\\s]')}\\}`;
        const regex = new RegExp(pattern, 'i');
        if (!regex.test(currentMsg)) {
            const formattedName = cleanedVar.replace(/_/g, ' ');
            missing.push(`{${formattedName}}`);
        }
    });

    return missing;
};

/**
 * Formats missing required variables into a human-readable error message.
 *
 * @param {Array<string>} missingArray - Array of missing formatted variable names e.g. ['{shop name}', '{reminder time}']
 * @returns {string} Error message string
 */
export const getMissingVariablesErrorMessage = (missingArray) => {
    if (!missingArray || missingArray.length === 0) return '';
    if (missingArray.length === 1) {
        return `This message is missing required variable: ${missingArray[0]}.`;
    }
    if (missingArray.length === 2) {
        return `This message is missing required variables: ${missingArray[0]} and ${missingArray[1]}.`;
    }
    const last = missingArray[missingArray.length - 1];
    const rest = missingArray.slice(0, -1).join(', ');
    return `This message is missing required variables: ${rest} and ${last}.`;
};
